import React, { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Button, Dialog, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { GridPreferencePanelsValue, useGridApiContext } from "@mui/x-data-grid";

import PageSizeSelect, { EXPANDED_PAGE_SIZES } from "./PageSizeSelect";

const DELOITTE_GREEN = "#86BC25";

// The community DataGrid has no apiRef prop, so this renders nothing inside the grid and hands its api out to the page
// (the Toolbar slot is the one place that gets props, and an empty one takes no room).
const GridApiCapture = ({ holder }) => {
  holder.current = useGridApiContext();
  return null;
};

// One slim row above a DataGrid: the bulk Actions menu (when rows are ticked) on the left, and Expand and the
// show / hide columns gear on the right. It replaces the grid's own toolbar row, so no space is lost. Expand opens the
// grid in a large rounded window.
//
// `children` is a function that gets the props to spread on the DataGrid (`components` to merge in, `componentsProps`)
// and returns the grid. It is rendered once: on the page, or in the window while that is open.
//   actions: [{ label, onClick, danger }]  — the bulk actions in the menu (none, or nothing selected: no Actions button)
//   rowCount — when 0 there is nothing to open, so Expand is disabled (with a tooltip saying why)
//   filtersSlot / chipsSlot — the page's Filters control and its active-filter chips, shown again at the top of the window
//   pageSize / defaultPageSize / onPageSizeChange / pageSizes — when given, the open window offers a rows-per-page
//   choice, and closing it puts the page back to the default size.
const ExpandableGrid = ({ title, subtitle, selectedCount = 0, actions = [], children, pageSize, defaultPageSize, onPageSizeChange, pageSizes = EXPANDED_PAGE_SIZES, filtersSlot, chipsSlot, rowCount }) => {
  const holder = useRef(null);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const closeExpanded = () => {
    setExpanded(false);
    if (onPageSizeChange && pageSize !== defaultPageSize) onPageSizeChange(defaultPageSize);
  };

  const toggleColumns = () => {
    const api = holder.current?.current;
    if (!api) return;
    const panel = api.state?.preferencePanel;
    if (panel?.open && panel.openedPanelValue === GridPreferencePanelsValue.columns) api.hidePreferences();
    else api.showPreferences(GridPreferencePanelsValue.columns);
  };

  const gridProps = {
    components: { Toolbar: GridApiCapture },
    componentsProps: { toolbar: { holder }, panel: { anchorEl: columnsAnchor, placement: "bottom-end" } },
  };

  const columnsButton = (
    <Tooltip title="Show / hide columns">
      <IconButton ref={setColumnsAnchor} size="small" aria-label="Show or hide columns" onClick={toggleColumns} sx={{ color: "#555" }}>
        <SettingsOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const actionsMenu =
    selectedCount > 0 && actions.length > 0 ? (
      <>
        <Button
          variant="contained"
          size="small"
          aria-haspopup="menu"
          aria-expanded={Boolean(actionsAnchor)}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setActionsAnchor(event.currentTarget)}
          sx={{ textTransform: "uppercase", fontSize: "12px", backgroundColor: DELOITTE_GREEN, color: "#fff", borderRadius: "6px", boxShadow: "none", "&:hover": { backgroundColor: "#76a821", boxShadow: "none" } }}
        >
          Actions ({selectedCount})
        </Button>
        <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
          {actions.map((action) => (
            <MenuItem
              key={action.label}
              sx={action.danger ? { color: "#d32f2f" } : undefined}
              onClick={() => {
                setActionsAnchor(null);
                action.onClick();
              }}
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
        <Typography sx={{ fontSize: "13px", color: "#666" }}>{selectedCount} selected</Typography>
      </>
    ) : null;

  const toolsRow = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 36, mb: 0.5 }}>
      {actionsMenu}
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
        {!expanded && (
          <Tooltip title={rowCount === 0 ? "Nothing to expand: the table has no rows" : "Open the table in a larger window"}>
            <span>
              <Button
                size="small"
                disabled={rowCount === 0}
                startIcon={<OpenInFullIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => setExpanded(true)}
                sx={{ textTransform: "none", fontSize: "13px", color: "#555", whiteSpace: "nowrap" }}
              >
                Expand
              </Button>
            </span>
          </Tooltip>
        )}
        {!expanded && columnsButton}
      </Box>
    </Box>
  );

  return (
    <>
      {!expanded && toolsRow}
      {!expanded && children(gridProps)}
      <Dialog
        open={expanded}
        onClose={closeExpanded}
        maxWidth={false}
        PaperProps={{ sx: { width: "calc(100vw - 48px)", height: "calc(100vh - 48px)", m: 0, borderRadius: "16px" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 600, fontSize: "18px" }}>
              {title}
              {subtitle && <Typography variant="subtitle1">{subtitle}</Typography>}
            </div>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {expanded && columnsButton}
              <Tooltip title="Exit full screen">
                <IconButton aria-label="Exit full screen" onClick={closeExpanded}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {chipsSlot && <Box sx={{ mt: 0.5 }}>{chipsSlot}</Box>}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 36, my: 1 }}>
            {actionsMenu}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
              {filtersSlot}
              {onPageSizeChange && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PageSizeSelect value={pageSize} onChange={onPageSizeChange} options={pageSizes} />
                </Box>
              )}
            </Box>
          </Box>
          {expanded && <div style={{ height: "calc(100vh - 230px)", width: "100%" }}>{children(gridProps)}</div>}
        </Box>
      </Dialog>
    </>
  );
};

export default ExpandableGrid;
